import { useTankRundown } from '@components/_SiteWide/TankDisplay/hooks/useTankRundown'
import api from '@utils/api'
import { sortNumeric } from '@utils/general'
import React, { createContext, useContext, useEffect, useState } from 'react'

import { addImpactToTanks } from '../util'

const DestinationsContext = createContext(undefined)

export function DestinationsContextProvider({ children, defaultStore, selectedOrder }) {
  const [stores, setStores] = useState([])
  const [orderDestinations, setOrderDestinations] = useState<Gravitate.OrderDestinations>([])
  const [storesLoading, setStoresLoading] = useState(false)

  const { selectedTank, setSelectedTank, selectedManifold, setSelectedManifold, tankRundownData, setTankRundownData } =
    useTankRundown({
      store: orderDestinations[0],
      shouldDefaultTankSelection: !selectedOrder,
    })

  useEffect(() => {
    let isMounted = true
    api.getStoreView().then((resp) => {
      isMounted && setStores(sortNumeric(resp?.data, 'store_number'))
    })
    return () => (isMounted = false)
  }, [])

  const updateDestinations = async (index, store, updateVolumes, replaceContainers) => {
    const copy = [...orderDestinations]

    copy[index] = store
    replaceContainers('N/A', store, index, copy, getImpactedTankVolumes, setOrderDestinations)
  }
  const removeUnusedDestinations = (compartments) => {
    const nonUniqueStores = []
    compartments.forEach((c) => c.targets.forEach((d) => nonUniqueStores.push(d.location_name)))
    const usedStores = [...new Set(nonUniqueStores)]
    const destCopy = [...orderDestinations]
    const filteredDests = destCopy.filter((d) => usedStores.includes(d.store_number))
    setOrderDestinations(filteredDests)
    return filteredDests
  }
  const removeDestination = (index, compartments, setCompartments) => {
    const storeNumber = orderDestinations[index].store_number
    const destCopy = [...orderDestinations]
    destCopy.splice(index, 1)
    const compartmentsCopy = [...compartments]
    const newCompartments = []
    compartmentsCopy.forEach((c) => {
      if (c) {
        c.targets.forEach((target, i) => {
          if (target.location_name === storeNumber) {
            if (i === 1) {
              c.targets[0].volume += target.volume
              c.targets.pop()
            } else {
              target.location_name = 'N/A'
              target.is_error = true
            }
          }
        })
      }

      newCompartments.push(c)
    })

    const impactedDestinations = getImpactedTankVolumes(newCompartments, destCopy)
    setCompartments(newCompartments)
    if (destCopy.length > 0) {
      setSelectedTank({ ...destCopy[0].tanks[0], store_number: destCopy[0].store_number })
    } else {
      setSelectedTank(undefined)
    }

    setOrderDestinations(impactedDestinations)
  }

  const setUpDestinations = async (order) => {
    let storeIdsForRetrieval = []
    if (order) {
      storeIdsForRetrieval = order.drops.map((d) => d.location_name)
    } else if (defaultStore) storeIdsForRetrieval = [defaultStore.store_number]

    if (storeIdsForRetrieval.length > 0 && order?.type?.toLowerCase() !== 'backhaul') {
      setStoresLoading(true)
      const resp = await api.getStoresByNumber(storeIdsForRetrieval)
      setStoresLoading(false)

      if (resp) {
        const [responseStoreOne, responseStoreTwo] = resp.data
        if (responseStoreOne) {
          order && addImpactToTanks(responseStoreOne, order)
          if (responseStoreTwo) {
            order && addImpactToTanks(responseStoreTwo, order)
          }
        }
        resp && setOrderDestinations(resp.data)
        return [responseStoreOne, responseStoreTwo]
      }
    }
    return []
  }
  const resetDefaultStore = async (updateVolumes) => {
    const resp = await api.getStoresByNumber([defaultStore.store_number])
    resp.data[0] && setOrderDestinations([...resp.data])
    resp.data[0] && setSelectedTank({ ...resp.data[0].tanks[0], store_number: resp.data[0].store_number })
  }
  const getImpactedTankVolumes = (compartments, destinations) => {
    if (destinations?.length === 0 && orderDestinations?.length === 0) return
    const destCopy = destinations ? [...destinations] : [...orderDestinations]

    destCopy.forEach((store) => {
      store.grouped_tanks.forEach((group) => {
        group.tanks.forEach((tank) => {
          const affectingCompartments = compartments.filter(
            (c) =>
              c &&
              c.targets.some((t) => t.location_name === store.store_number && t.tank_id === tank.tank_id) &&
              !c.is_error
          )
          let volume = 0

          affectingCompartments.forEach((c) => {
            const applicableTarget = c.targets.find(
              (t) => t.location_name === store.store_number && t.tank_id === tank.tank_id
            )
            if (applicableTarget && applicableTarget.volume !== '') {
              volume += applicableTarget.volume
            }
          })

          if (volume) {
            if (tank.manifold_id) {
              const manifold = group.manifold_tanks.find((manifold) => manifold.manifold_id === tank.manifold_id)
              const manifoldChildTank = manifold.tanks.find((manifoldTank) => manifoldTank.tank_id === tank.tank_id)
              manifoldChildTank.projected_volume = manifoldChildTank.current_volume + volume
              manifoldChildTank.projected_percent_filled = manifoldChildTank.projected_volume / tank.storage_max
            }
            tank.projected_volume = tank.current_volume + volume
            tank.projected_percent_filled = tank.projected_volume / tank.storage_max
          } else {
            if (tank.manifold_id) {
              const manifold = group.manifold_tanks.find((manifold) => manifold.manifold_id === tank.manifold_id)
              const manifoldChildTank = manifold.tanks.find((manifoldTank) => manifoldTank.tank_id === tank.tank_id)
              manifoldChildTank.projected_percent_filled = null
              manifoldChildTank.projected_volume = null
            }
            tank.projected_percent_filled = null
            tank.projected_volume = null
          }
        })
      })
    })

    return destCopy
  }
  return (
    <DestinationsContext.Provider
      value={{
        stores,
        storesLoading,
        resetDefaultStore,
        getImpactedTankVolumes,
        updateDestinations,
        removeDestination,
        removeUnusedDestinations,
        orderDestinations,
        setOrderDestinations,
        setUpDestinations,
        defaultStore,
        selectedTank,
        setSelectedTank,
        selectedManifold,
        setSelectedManifold,
        tankRundownData,
        setTankRundownData,
      }}
    >
      {children}
    </DestinationsContext.Provider>
  )
}

export const useDestinationsContext = () => {
  const context = useContext(DestinationsContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
